'use client';

import { c as _c } from "react/compiler-runtime";
import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CuteSpinner } from '@/components/common/CuteSpinner';
import { createEntityAnchorTransform } from '@/components/markdown/entity-anchors';
import type { StreamdownAnchorTransform } from '@/components/markdown/StreamdownRenderer';
import { SummarizerPanel } from '@/components/summarizer/SummarizerPanel';
import { buildSummaryDiagnosticsLines } from '@/components/summarizer/summary-diagnostics';
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { collectConversationSummaryImages } from '@/lib/media/summary-images';
import { createProfileLookup } from '@/lib/profile-lookup';
import { cn } from '@/lib/utils';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import { MIN_CONVERSATION_TWEETS_FOR_SUMMARY } from './summary/constants';
import { formatSummaryForDisplay } from './summary/summary-utils';
import { useConversationSummaryChat } from './summary/useConversationSummaryChat';
import { useConversationSummaryStream } from './summary/useConversationSummaryStream';
import type { ConversationThreadResult } from './useConversationThread';
type ConversationProfiles = ConversationThreadResult['profiles'];
const EMPTY_CONVERSATION_PROFILES: ConversationProfiles = {};
function normalizeConversationProfiles(profiles: ConversationProfiles | null | undefined): ConversationProfiles {
  return profiles ?? EMPTY_CONVERSATION_PROFILES;
}
interface ConversationSummaryProperties {
  className?: string | undefined;
  thread: ConversationThreadResult;
  tweetId: string;
}
export function ConversationSummary(t0) {
  const $ = _c(117);
  const {
    tweetId,
    thread,
    className
  } = t0;
  const [isSummaryDismissed, setIsSummaryDismissed] = useState(false);
  let t1;
  if ($[0] !== thread.tweetIds || $[1] !== thread.tweets) {
    t1 = collectConversationSummaryImages(thread.tweetIds, thread.tweets);
    $[0] = thread.tweetIds;
    $[1] = thread.tweets;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const summaryImages = t1;
  const summaryImageCount = summaryImages.length;
  const hasImages = summaryImageCount > 0;
  const conversation = thread.conversation;
  const totalTweetCount = thread.tweetIds.length;
  let t2;
  if ($[3] !== thread.profiles) {
    t2 = normalizeConversationProfiles(thread.profiles);
    $[3] = thread.profiles;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const profiles = t2;
  const rootId = conversation.rootTweetId;
  const canonicalSummaryTweetId = typeof rootId === "string" && rootId.length > 0 ? rootId : tweetId;
  let t3;
  bb0: {
    const rootTweet = thread.tweets[conversation.rootTweetId];
    if (!rootTweet) {
      t3 = null;
      break bb0;
    }
    const profileRecord = rootTweet.authorId ? profiles[rootTweet.authorId] : null;
    const username = profileRecord?.username ?? rootTweet.author?.username ?? null;
    if (!username) {
      t3 = null;
      break bb0;
    }
    const t4 = profileRecord?.name ?? rootTweet.author?.name ?? null;
    const t5 = profileRecord?.profileImageUrl ?? rootTweet.author?.profileImageUrl ?? null;
    let t6;
    if ($[5] !== t4 || $[6] !== t5 || $[7] !== username) {
      t6 = {
        username,
        name: t4,
        profileImageUrl: t5
      };
      $[5] = t4;
      $[6] = t5;
      $[7] = username;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    t3 = t6;
  }
  const rootProfile = t3;
  let t4;
  if ($[9] !== profiles) {
    const records = Object.values(profiles);
    t4 = createProfileLookup(records.map(_temp));
    $[9] = profiles;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const profileLookup = t4;
  let entries;
  if ($[11] !== profiles || $[12] !== thread.tweets) {
    entries = new Map();
    let t5;
    if ($[14] !== thread.tweets) {
      t5 = Object.values(thread.tweets ?? {});
      $[14] = thread.tweets;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    for (const tweet of t5) {
      if (!tweet?.id) {
        continue;
      }
      const profileRecord_0 = tweet.authorId ? profiles[tweet.authorId] : null;
      entries.set(tweet.id, {
        authorUsername: tweet.author?.username ?? profileRecord_0?.username ?? null,
        authorName: tweet.author?.name ?? profileRecord_0?.name ?? null,
        authorProfileImageUrl: tweet.author?.profileImageUrl ?? profileRecord_0?.profileImageUrl ?? null
      });
    }
    $[11] = profiles;
    $[12] = thread.tweets;
    $[13] = entries;
  } else {
    entries = $[13];
  }
  const tweetLookup = entries;
  let t5;
  if ($[16] !== profileLookup || $[17] !== tweetLookup) {
    t5 = createEntityAnchorTransform({
      profileLookup,
      resolveProfileHref: _temp2,
      profileLinkTarget: "_self",
      avatarSize: 18,
      tweetLookup,
      tweetAvatarSize: 18,
      tweetAvatarClassName: "flex-shrink-0"
    });
    $[16] = profileLookup;
    $[17] = tweetLookup;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const anchorTransform = t5;
  let t6;
  if ($[19] !== conversation.missingTweets) {
    t6 = conversation.missingTweets === undefined ? {} : {
      missingTweets: conversation.missingTweets
    };
    $[19] = conversation.missingTweets;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== conversation.dataSources || $[22] !== conversation.id || $[23] !== conversation.isComplete || $[24] !== conversation.lastActivityAt || $[25] !== conversation.maxDepth || $[26] !== conversation.participantCount || $[27] !== conversation.rootTweetId || $[28] !== conversation.tweetCount || $[29] !== t6) {
    t7 = {
      id: conversation.id,
      rootTweetId: conversation.rootTweetId,
      tweetCount: conversation.tweetCount,
      participantCount: conversation.participantCount,
      maxDepth: conversation.maxDepth,
      lastActivityAt: conversation.lastActivityAt,
      dataSources: conversation.dataSources,
      isComplete: conversation.isComplete,
      ...t6
    };
    $[21] = conversation.dataSources;
    $[22] = conversation.id;
    $[23] = conversation.isComplete;
    $[24] = conversation.lastActivityAt;
    $[25] = conversation.maxDepth;
    $[26] = conversation.participantCount;
    $[27] = conversation.rootTweetId;
    $[28] = conversation.tweetCount;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const conversationPayload = t7;
  let t8;
  if ($[31] !== canonicalSummaryTweetId || $[32] !== hasImages || $[33] !== summaryImageCount) {
    t8 = {
      canonicalTweetId: canonicalSummaryTweetId,
      hasImages,
      summaryImageCount
    };
    $[31] = canonicalSummaryTweetId;
    $[32] = hasImages;
    $[33] = summaryImageCount;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  const {
    summaryState,
    consideredTweets,
    usedImages,
    handleSummarize: runSummary,
    isSummarizing
  } = useConversationSummaryStream(t8);
  const tweetCount = thread.conversation.tweetCount ?? thread.tweetIds.length;
  const canSummarize = tweetCount >= MIN_CONVERSATION_TWEETS_FOR_SUMMARY;
  let t9;
  if ($[35] !== canSummarize || $[36] !== isSummarizing || $[37] !== runSummary) {
    t9 = async () => {
      if (!canSummarize || isSummarizing) {
        return;
      }
      setIsSummaryDismissed(false);
      await runSummary();
    };
    $[35] = canSummarize;
    $[36] = isSummarizing;
    $[37] = runSummary;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const handleSummarize = t9;
  let t10;
  if ($[39] !== handleSummarize) {
    t10 = () => {
      handleSummarize().catch(_temp3);
    };
    $[39] = handleSummarize;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  const triggerSummarize = t10;
  let t11;
  if ($[41] !== summaryState.text) {
    t11 = formatSummaryForDisplay(summaryState.text);
    $[41] = summaryState.text;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  const formattedSummaryText = t11;
  let t12;
  bb1: {
    if (typeof consideredTweets === "number") {
      t12 = consideredTweets;
      break bb1;
    }
    if (typeof conversation.tweetCount === "number") {
      t12 = conversation.tweetCount;
      break bb1;
    }
    t12 = totalTweetCount;
  }
  const resolvedConsideredTweetCount = t12;
  let t13;
  bb2: {
    if (typeof usedImages === "number") {
      t13 = usedImages;
      break bb2;
    }
    t13 = hasImages ? summaryImageCount : 0;
  }
  const resolvedImageCount = t13;
  let t14;
  if ($[43] !== canonicalSummaryTweetId || $[44] !== conversationPayload || $[45] !== resolvedConsideredTweetCount || $[46] !== resolvedImageCount || $[47] !== rootProfile || $[48] !== summaryImages || $[49] !== summaryState.text || $[50] !== thread || $[51] !== totalTweetCount) {
    t14 = {
      canonicalSummaryTweetId,
      summaryText: summaryState.text,
      summaryImages,
      thread,
      resolvedConsideredTweetCount,
      resolvedImageCount,
      totalTweetCount,
      conversation: conversationPayload,
      rootProfile
    };
    $[43] = canonicalSummaryTweetId;
    $[44] = conversationPayload;
    $[45] = resolvedConsideredTweetCount;
    $[46] = resolvedImageCount;
    $[47] = rootProfile;
    $[48] = summaryImages;
    $[49] = summaryState.text;
    $[50] = thread;
    $[51] = totalTweetCount;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  const {
    canChat,
    isChatEnabled,
    isChatLoading,
    handleChat,
    copySummary
  } = useConversationSummaryChat(t14);
  const t15 = `Tweets analyzed: ${resolvedConsideredTweetCount}`;
  const t16 = `Participants: ${conversation.participantCount}`;
  const t17 = `Max depth: ${conversation.maxDepth}`;
  let preamble;
  if ($[53] !== conversation.isComplete || $[54] !== conversation.lastActivityAt || $[55] !== conversation.missingTweets || $[56] !== t15 || $[57] !== t16 || $[58] !== t17) {
    preamble = [t15, t16, t17];
    if (conversation.lastActivityAt) {
      let t18;
      if ($[60] !== conversation.lastActivityAt) {
        t18 = conversation.lastActivityAt.toLocaleString();
        $[60] = conversation.lastActivityAt;
        $[61] = t18;
      } else {
        t18 = $[61];
      }
      preamble.push(`Last activity: ${t18}`);
    }
    if (conversation.missingTweets) {
      preamble.push("Note: Some tweets are still loading.");
    } else {
      if (!conversation.isComplete) {
        preamble.push("Conversation snapshot may be incomplete.");
      }
    }
    $[53] = conversation.isComplete;
    $[54] = conversation.lastActivityAt;
    $[55] = conversation.missingTweets;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = preamble;
  } else {
    preamble = $[59];
  }
  let t18;
  if ($[62] !== preamble || $[63] !== summaryState.metadata) {
    const diagnostics = buildSummaryDiagnosticsLines({
      metadata: summaryState.metadata,
      modeLabel: "Summary",
      canonicalModelFallback: null,
      powerUserEnabled: true
    });
    t18 = [...preamble, ...diagnostics];
    $[62] = preamble;
    $[63] = summaryState.metadata;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  const summaryHelpContent = t18.join("\n");
  let t19;
  if ($[65] !== summaryState.text) {
    t19 = toTrimmedNonEmptyString(summaryState.text);
    $[65] = summaryState.text;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  const summaryText = t19;
  const hasSummary = summaryText !== null;
  const shouldShowPrimaryButton = isSummaryDismissed || !hasSummary || summaryState.status === "error";
  const showSummaryCard = !isSummaryDismissed && (summaryState.status !== "idle" || hasSummary || Boolean(summaryState.error));
  let primaryButtonLabel = "Summarize conversation";
  if (isSummarizing) {
    primaryButtonLabel = "Summarizing\u2026";
  } else {
    if (summaryState.status === "error") {
      primaryButtonLabel = "Try again";
    } else {
      if (isSummaryDismissed && hasSummary) {
        primaryButtonLabel = "Show summary";
      }
    }
  }
  let t20;
  if ($[67] !== hasSummary || $[68] !== isSummaryDismissed || $[69] !== triggerSummarize) {
    t20 = event => {
      event.preventDefault();
      event.stopPropagation();
      if (isSummaryDismissed && hasSummary) {
        setIsSummaryDismissed(false);
        return;
      }
      setIsSummaryDismissed(false);
      triggerSummarize();
    };
    $[67] = hasSummary;
    $[68] = isSummaryDismissed;
    $[69] = triggerSummarize;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  const handlePrimaryButtonClick = t20;
  let t21;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => {
      setIsSummaryDismissed(true);
    };
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  const handleDismiss = t21;
  let t22;
  if ($[72] !== canChat || $[73] !== handleChat) {
    t22 = event_0 => {
      event_0.preventDefault();
      event_0.stopPropagation();
      if (!canChat) {
        return;
      }
      handleChat();
    };
    $[72] = canChat;
    $[73] = handleChat;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  const handleChatMenuSelect = t22;
  let t23;
  if ($[75] !== copySummary || $[76] !== hasSummary) {
    t23 = event_1 => {
      event_1.preventDefault();
      event_1.stopPropagation();
      if (!hasSummary) {
        return;
      }
      copySummary().then(_temp4).catch(_temp5);
    };
    $[75] = copySummary;
    $[76] = hasSummary;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  const handleCopySummaryMenuSelect = t23;
  let t24;
  if ($[78] !== canChat || $[79] !== handleChatMenuSelect || $[80] !== handleCopySummaryMenuSelect || $[81] !== hasSummary || $[82] !== isChatLoading) {
    t24 = () => <DropdownMenuContent align="end" className="w-56"><DropdownMenuItem disabled={!canChat} onSelect={handleChatMenuSelect}>{isChatLoading ? <span className="inline-flex items-center gap-2"><CuteSpinner size={12} className="border-current/30 border-t-current" aria-hidden="true" />Preparing chat…</span> : "Chat about conversation"}</DropdownMenuItem><DropdownMenuSeparator /><DropdownMenuItem disabled={!hasSummary} onSelect={handleCopySummaryMenuSelect}>Copy summary</DropdownMenuItem></DropdownMenuContent>;
    $[78] = canChat;
    $[79] = handleChatMenuSelect;
    $[80] = handleCopySummaryMenuSelect;
    $[81] = hasSummary;
    $[82] = isChatLoading;
    $[83] = t24;
  } else {
    t24 = $[83];
  }
  const renderPrimaryActionMenu = t24;
  let t25;
  bb3: {
    if (summaryState.status === "error") {
      let t26;
      if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = [];
        $[84] = t26;
      } else {
        t26 = $[84];
      }
      t25 = t26;
      break bb3;
    }
    const t26 = `${resolvedConsideredTweetCount} ${resolvedConsideredTweetCount === 1 ? "tweet" : "tweets"}`;
    let items;
    if ($[85] !== resolvedImageCount || $[86] !== t26) {
      items = [t26];
      if (resolvedImageCount > 0) {
        items.push(`${resolvedImageCount} ${resolvedImageCount === 1 ? "image" : "images"}`);
      }
      $[85] = resolvedImageCount;
      $[86] = t26;
      $[87] = items;
    } else {
      items = $[87];
    }
    t25 = items;
  }
  const summaryMetadataItems = t25;
  let t26;
  bb4: {
    if (summaryMetadataItems.length === 0) {
      t26 = undefined;
      break bb4;
    }
    const t27 = summaryMetadataItems.join(" \u2022 ");
    let t28;
    if ($[88] !== t27) {
      t28 = t27.toUpperCase();
      $[88] = t27;
      $[89] = t28;
    } else {
      t28 = $[89];
    }
    let t29;
    if ($[90] !== t28) {
      t29 = <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground whitespace-nowrap">{t28}</span>;
      $[90] = t28;
      $[91] = t29;
    } else {
      t29 = $[91];
    }
    t26 = t29;
  }
  const headerLeadingActions = t26;
  if (!canSummarize) {
    return null;
  }
  let t27;
  if ($[92] !== handlePrimaryButtonClick || $[93] !== isSummarizing || $[94] !== primaryButtonLabel || $[95] !== renderPrimaryActionMenu || $[96] !== shouldShowPrimaryButton) {
    t27 = shouldShowPrimaryButton ? {
      align: "center" as const,
      label: primaryButtonLabel,
      onClick: handlePrimaryButtonClick,
      disabled: isSummarizing,
      secondaryAction: {
        type: "menu" as const,
        ariaLabel: "Conversation summary actions",
        renderMenu: renderPrimaryActionMenu
      }
    } : undefined;
    $[92] = handlePrimaryButtonClick;
    $[93] = isSummarizing;
    $[94] = primaryButtonLabel;
    $[95] = renderPrimaryActionMenu;
    $[96] = shouldShowPrimaryButton;
    $[97] = t27;
  } else {
    t27 = $[97];
  }
  const panelPrimaryAction = t27;
  let t28;
  if ($[98] !== className) {
    t28 = cn("mt-4", className);
    $[98] = className;
    $[99] = t28;
  } else {
    t28 = $[99];
  }
  const t29 = isChatEnabled ? handleChat : undefined;
  let t30;
  if ($[100] !== anchorTransform || $[101] !== formattedSummaryText || $[102] !== isChatEnabled || $[103] !== isChatLoading || $[104] !== isSummarizing || $[105] !== summaryHelpContent || $[106] !== summaryState.error || $[107] !== summaryState.status || $[108] !== t29 || $[109] !== triggerSummarize) {
    t30 = {
      className: "border-primary/30 bg-primary/10 text-muted-foreground",
      status: summaryState.status,
      text: formattedSummaryText,
      markdown: true,
      error: summaryState.error,
      title: "Conversation Summary",
      transformAnchor: anchorTransform,
      textClassName: "leading-relaxed text-card-foreground break-words",
      textWrapperClassName: "text-sm",
      generatingLabel: "Generating summary\u2026",
      emptyLabel: "No summary available yet.",
      helpContent: summaryHelpContent,
      onDismiss: handleDismiss,
      dismissTooltip: "Hide summary",
      onChat: t29,
      isChatEnabled,
      isChatLoading,
      onRetry: triggerSummarize,
      retryLabel: "Try again",
      isRetryDisabled: isSummarizing
    };
    $[100] = anchorTransform;
    $[101] = formattedSummaryText;
    $[102] = isChatEnabled;
    $[103] = isChatLoading;
    $[104] = isSummarizing;
    $[105] = summaryHelpContent;
    $[106] = summaryState.error;
    $[107] = summaryState.status;
    $[108] = t29;
    $[109] = triggerSummarize;
    $[110] = t30;
  } else {
    t30 = $[110];
  }
  let t31;
  if ($[111] !== headerLeadingActions || $[112] !== panelPrimaryAction || $[113] !== showSummaryCard || $[114] !== t28 || $[115] !== t30) {
    t31 = <SummarizerPanel className={t28} dataTestId="conversation-summary" primaryAction={panelPrimaryAction} cardProps={t30} headerLeadingActions={headerLeadingActions} showCard={showSummaryCard} />;
    $[111] = headerLeadingActions;
    $[112] = panelPrimaryAction;
    $[113] = showSummaryCard;
    $[114] = t28;
    $[115] = t30;
    $[116] = t31;
  } else {
    t31 = $[116];
  }
  return t31;
}
function _temp5(error_0) {
  const message = error_0 instanceof Error ? error_0.message : "Unable to copy summary.";
  toast.error(message);
}
function _temp4() {
  return toast.success("Summary copied to clipboard.");
}
function _temp3(error) {
  console.error("Failed to summarize conversation", error);
}
function _temp2(username_0) {
  return `/@${username_0}`;
}
function _temp(profile) {
  return {
    username: profile?.username ?? null,
    name: profile?.name ?? null,
    profileImageUrl: profile?.profileImageUrl ?? null
  };
}